import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  Activity,
  BriefcaseBusiness,
  FileUser,
  MessageSquareText,
  PhoneCall,
  Trophy,
  UserRound,
} from "lucide-react";
import { adminApi } from "@/api/admin.api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AdminEntityActions } from "./AdminEntityActions";
import { useAdminProduct } from "./hooks";

type ResourceType =
  | "resumes"
  | "practice-interviews"
  | "recruiter-interviews"
  | "applications"
  | "recruiter-calls"
  | "usage"
  | "activity";

const RESOURCE_TABS: Array<{ value: ResourceType; label: string }> = [
  { value: "resumes", label: "Resumes" },
  { value: "practice-interviews", label: "Practice interviews" },
  { value: "recruiter-interviews", label: "Recruiter interviews" },
  { value: "applications", label: "Applications" },
  { value: "recruiter-calls", label: "Recruiter calls" },
  { value: "usage", label: "Usage" },
  { value: "activity", label: "Activity" },
];

const STATUS_OPTIONS: Partial<Record<ResourceType, string[]>> = {
  resumes: ["indexed", "not-indexed", "public", "private"],
  "practice-interviews": ["IN_PROGRESS", "COMPLETED", "ARCHIVED"],
  "recruiter-interviews": ["PENDING", "SCHEDULED", "IN_PROGRESS", "COMPLETED", "CANCELLED"],
  applications: [
    "APPLIED",
    "SHORTLISTED",
    "INTERVIEW_SCHEDULED",
    "INTERVIEW_DONE",
    "ON_HOLD",
    "OFFERED",
    "REJECTED",
    "JOB_OPENING_CLOSED",
  ],
  "recruiter-calls": [
    "QUEUED",
    "DIALING",
    "RINGING",
    "ANSWERED",
    "FAILED",
    "COMPLETED",
    "NO_ANSWER",
    "BUSY",
  ],
  usage: ["success", "failed"],
  activity: ["read", "unread", "DRAFT", "PROCESSING", "PUBLISHED", "FAILED"],
};

export function UserDetails() {
  const { id } = useParams();
  const { product } = useAdminProduct();
  const [activeResource, setActiveResource] = useState<ResourceType>("resumes");
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const overviewQuery = useQuery({
    queryKey: ["admin-user-overview", id],
    queryFn: () => adminApi.getUserOverview(id!),
    enabled: Boolean(id),
  });
  const resourceQuery = useQuery({
    queryKey: ["admin-user-resource", id, activeResource, page, status, product, dateFrom, dateTo],
    queryFn: () =>
      adminApi.getUserResource(id!, activeResource, {
        page,
        limit: 20,
        status: status || undefined,
        product: activeResource === "usage" ? product : undefined,
        dateFrom: dateFrom ? new Date(`${dateFrom}T00:00:00`).toISOString() : undefined,
        dateTo: dateTo ? new Date(`${dateTo}T23:59:59.999`).toISOString() : undefined,
      }),
    enabled: Boolean(id),
  });

  useEffect(() => {
    setPage(1);
    setStatus("");
  }, [activeResource]);

  const overview = overviewQuery.data;
  const user = overview?.user;
  const resource = resourceQuery.data;
  const stats = useMemo(
    () => [
      { label: "Resumes", value: overview?.counts?.resumes ?? 0, icon: FileUser },
      {
        label: "Practice completed",
        value: `${overview?.counts?.completedPracticeInterviews ?? 0}/${
          overview?.counts?.practiceInterviews ?? 0
        }`,
        icon: MessageSquareText,
      },
      {
        label: "Recruiter interviews",
        value: `${overview?.counts?.completedRecruiterInterviews ?? 0}/${
          overview?.counts?.recruiterInterviews ?? 0
        }`,
        icon: BriefcaseBusiness,
      },
      { label: "Applications", value: overview?.counts?.applications ?? 0, icon: Activity },
      { label: "Offers", value: overview?.counts?.offers ?? 0, icon: Trophy },
      { label: "Recruiter calls", value: overview?.counts?.recruiterCalls ?? 0, icon: PhoneCall },
      {
        label: "Unread notifications",
        value: overview?.counts?.unreadNotifications ?? 0,
        icon: UserRound,
      },
      {
        label: "Failed usage",
        value: overview?.counts?.failedUsageEvents ?? 0,
        icon: Activity,
      },
    ],
    [overview]
  );

  if (overviewQuery.isLoading)
    return <PageMessage message="Loading candidate investigation data..." />;
  if (overviewQuery.isError)
    return <PageMessage message="Unable to load candidate investigation data." error />;
  if (!user) return <PageMessage message="Candidate not found." error />;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold text-slate-950">{user.name || user.email}</h1>
            <Badge variant={user.emailVerified ? "success" : "warning"}>
              {user.emailVerified ? "Verified" : "Unverified"}
            </Badge>
            <Badge variant="outline">Investigation</Badge>
          </div>
          <p className="text-sm text-slate-600">{user.email}</p>
          <p className="mt-1 font-mono text-xs text-slate-500">User ID: {user.id}</p>
        </div>
        <div className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Investigation data is read-only. Administrative actions are separated below.
        </div>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map(({ label, value, icon: Icon }) => (
          <Card key={label}>
            <CardContent className="flex items-center justify-between p-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {label}
                </p>
                <p className="mt-1 text-2xl font-semibold text-slate-950">{value}</p>
              </div>
              <Icon className="h-5 w-5 text-slate-400" />
            </CardContent>
          </Card>
        ))}
      </section>

      <section className="grid gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle>Account and profile</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 text-sm sm:grid-cols-2">
            <Detail label="Username" value={user.username} mono />
            <Detail label="Provider" value={user.provider} />
            <Detail label="Role" value={user.role} />
            <Detail label="Locale" value={user.locale} />
            <Detail
              label="Two-factor authentication"
              value={user.twoFactorEnabled ? "Enabled" : "Disabled"}
            />
            <Detail label="Last sign-in" value={formatDateTime(user.secrets?.lastSignedIn)} />
            <Detail label="Created" value={formatDateTime(user.createdAt)} />
            <Detail label="Updated" value={formatDateTime(user.updatedAt)} />
            <Detail label="Career page" value={user.careerPage?.visibility || "Not configured"} />
            <Detail
              label="Availability"
              value={user.careerPage?.availabilityStatus || "Not configured"}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Career progress</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Detail label="Career page views" value={String(user.careerPage?.viewCount ?? 0)} />
            <Detail label="Level" value={String(user.gameProfile?.level ?? 0)} />
            <Detail label="Total XP" value={String(user.gameProfile?.totalXP ?? 0)} />
            <Detail
              label="Current streak"
              value={`${user.gameProfile?.currentStreakDays ?? 0} days`}
            />
            <Detail label="Resume downloads" value={String(user.features?.downloadCount ?? 0)} />
            <Detail
              label="Legacy interview training"
              value={String(user.features?.interviewTrainingCount ?? 0)}
            />
          </CardContent>
        </Card>
      </section>

      <HealthCard flags={overview.healthFlags} />
      <BillingSummary billing={overview.billing} />
      <UsageSummary counters={overview.usageCounters} packs={overview.purchasedPacks} />

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Related records</CardTitle>
              <p className="mt-1 text-sm text-slate-500">
                Raw resumes, answers, transcripts, recordings, storage paths, and credentials are
                excluded.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                aria-label="Status filter"
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value);
                  setPage(1);
                }}
                className="h-9 rounded-sm border border-slate-300 bg-white px-3 text-sm"
              >
                <option value="">All statuses</option>
                {(STATUS_OPTIONS[activeResource] || []).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <input
                aria-label="From date"
                type="date"
                value={dateFrom}
                onChange={(event) => {
                  setDateFrom(event.target.value);
                  setPage(1);
                }}
                className="h-9 rounded-sm border border-slate-300 px-2 text-sm"
              />
              <input
                aria-label="To date"
                type="date"
                value={dateTo}
                onChange={(event) => {
                  setDateTo(event.target.value);
                  setPage(1);
                }}
                className="h-9 rounded-sm border border-slate-300 px-2 text-sm"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <TabsRoot
            value={activeResource}
            onValueChange={(value) => setActiveResource(value as ResourceType)}
          >
            <TabsList className="mb-4 flex h-auto flex-wrap justify-start">
              {RESOURCE_TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {RESOURCE_TABS.map((tab) => (
              <TabsContent key={tab.value} value={tab.value}>
                <ResourceTable
                  resource={tab.value}
                  data={resource}
                  loading={resourceQuery.isFetching}
                />
              </TabsContent>
            ))}
          </TabsRoot>
          <Pagination
            page={resource?.page || page}
            totalPages={resource?.totalPages || 1}
            total={resource?.total || 0}
            onPage={setPage}
          />
        </CardContent>
      </Card>

      <AdminEntityActions
        entityType="user"
        entityId={user.id}
        product={product}
        subscriptions={overview.billing.subscriptions}
        stripe={overview.billing.stripe}
      />
    </div>
  );
}

function HealthCard({ flags }: { flags: any[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Workflow health</CardTitle>
      </CardHeader>
      <CardContent>
        {!flags.length ? (
          <p className="text-sm text-slate-500">No workflow warnings detected.</p>
        ) : (
          <div className="grid gap-2 md:grid-cols-2">
            {flags.map((flag) => (
              <div
                key={flag.key}
                className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm"
              >
                <strong>{flag.count}</strong> · {flag.label}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BillingSummary({ billing }: { billing: any }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Billing overview</CardTitle>
          <Badge variant={billing.stripe.available ? "success" : "warning"}>
            Stripe {billing.stripe.available ? "available" : "unavailable"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <MiniCount label="Subscriptions" value={billing.subscriptions.length} />
          <MiniCount label="Legacy payments" value={billing.legacyPayments.length} />
          <MiniCount
            label="Lifetime value"
            value={`$${Number(billing.stripe.overview?.lifetimeValue || 0).toFixed(2)}`}
          />
        </div>
        {!billing.subscriptions.length ? (
          <Empty message="No subscriptions found." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Period</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {billing.subscriptions.map((subscription: any) => (
                <TableRow key={subscription.id}>
                  <TableCell>{subscription.plan.product}</TableCell>
                  <TableCell>{subscription.plan.displayName}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{subscription.status}</Badge>
                  </TableCell>
                  <TableCell>
                    {formatDate(subscription.currentPeriodStart)} -{" "}
                    {formatDate(subscription.currentPeriodEnd)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function UsageSummary({ counters, packs }: { counters: any[]; packs: any[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Entitlements and usage</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 lg:grid-cols-2">
        <SimpleList
          title="Current usage counters"
          items={counters.slice(0, 12).map((item) => ({
            key: item.id,
            title: item.featureKey,
            detail: `${item.usageCount} used${item.limit == null ? "" : ` of ${item.limit}`} · ${
              item.tier
            } · ends ${formatDate(item.periodEnd)}`,
          }))}
        />
        <SimpleList
          title="Purchased capacity"
          items={packs.slice(0, 12).map((item) => ({
            key: item.id,
            title: item.featureKey,
            detail: `${item.remaining} remaining of ${item.quantity} · ${
              item.isActive ? "active" : "inactive"
            } · expires ${formatDate(item.expiresAt)}`,
          }))}
        />
      </CardContent>
    </Card>
  );
}

function SimpleList({
  title,
  items,
}: {
  title: string;
  items: Array<{ key: string; title: string; detail: string }>;
}) {
  return (
    <div className="rounded-sm border border-slate-200 p-4">
      <h3 className="mb-3 text-sm font-semibold">{title}</h3>
      {!items.length ? (
        <p className="text-sm text-slate-500">No records.</p>
      ) : (
        items.map((item) => (
          <div key={item.key} className="mb-3 last:mb-0">
            <p className="text-sm font-medium">{item.title}</p>
            <p className="text-xs text-slate-500">{item.detail}</p>
          </div>
        ))
      )}
    </div>
  );
}

function ResourceTable({
  resource,
  data,
  loading,
}: {
  resource: ResourceType;
  data: any;
  loading: boolean;
}) {
  if (loading && !data) return <PageMessage message="Loading related records..." />;
  if (!data?.items?.length)
    return <Empty message="No related records found for the selected filters." />;
  const renderers: Record<
    ResourceType,
    { headers: string[]; cells: (item: any) => React.ReactNode[] }
  > = {
    resumes: {
      headers: ["Resume", "Visibility", "Indexed", "ATS score", "Usage", "Updated"],
      cells: (item) => [
        <div>
          <p className="font-medium">{item.title}</p>
          <p className="text-xs text-slate-500">
            {item.jobTitle || "No target role"} · {item.jobCompanyName || "No target company"}
          </p>
        </div>,
        item.visibility,
        item.searchIndexed ? "Indexed" : "Not indexed",
        item.atsFinalScore ?? "—",
        `${item._count.InterviewPracticeSession} practices · ${item._count.spotlightIntros} intros`,
        formatDateTime(item.updatedAt),
      ],
    },
    "practice-interviews": {
      headers: ["Practice", "Status", "Questions", "Score", "Analysis", "Created"],
      cells: (item) => [
        <div>
          <p className="font-medium">
            {item.targetRole || item.practiceFocus || item.practiceType}
          </p>
          <p className="text-xs text-slate-500">
            {item.targetCompany || "No target company"} · {item.difficulty}
          </p>
        </div>,
        <Badge variant="outline">{item.status}</Badge>,
        `${item.questionsCompleted ?? item._count.responses}/${
          item.questionCount ?? item._count.questions
        }`,
        item.overallScore == null ? "—" : Number(item.overallScore).toFixed(1),
        item.analysisStatus,
        formatDateTime(item.createdAt),
      ],
    },
    "recruiter-interviews": {
      headers: ["Job", "Recruiter", "Status", "Responses", "Score", "Scheduled"],
      cells: (item) => [
        <div>
          <p className="font-medium">{item.jobPosting.title}</p>
          <p className="text-xs text-slate-500">{item.jobPosting.companyName}</p>
        </div>,
        item.recruiter.name || item.recruiter.company,
        <Badge variant="outline">{item.status}</Badge>,
        item._count.responses,
        item.overallScore ?? "—",
        formatDateTime(item.scheduledAt),
      ],
    },
    applications: {
      headers: ["Job", "Candidate status", "Recruiter status", "Match", "Interviews", "Updated"],
      cells: (item) => [
        <div>
          <p className="font-medium">{item.jobTitle}</p>
          <p className="text-xs text-slate-500">{item.companyName}</p>
        </div>,
        <Badge variant="outline">{item.candidateStatus}</Badge>,
        item.promotedApplication?.candidateStatus || "Not promoted",
        item.promotedApplication?.matchScore == null
          ? "—"
          : `${Math.round(item.promotedApplication.matchScore)}%`,
        item.promotedApplication?._count.interviewRequests ?? 0,
        formatDateTime(item.updatedAt),
      ],
    },
    "recruiter-calls": {
      headers: ["Job", "Company", "Status", "Duration", "Recruiter", "Created"],
      cells: (item) => [
        item.jobTitle || shortId(item.jobId),
        item.companyName || "—",
        <Badge variant="outline">{item.status}</Badge>,
        item.callDuration == null ? "—" : `${item.callDuration}s`,
        shortId(item.recruiterId),
        formatDateTime(item.createdAt),
      ],
    },
    usage: {
      headers: ["Feature", "Event", "Result", "Resource", "Error", "Created"],
      cells: (item) => [
        item.featureKey,
        item.eventType,
        <Badge variant={item.success ? "success" : "warning"}>
          {item.success ? "Success" : "Failed"}
        </Badge>,
        item.resourceType ? `${item.resourceType}: ${shortId(item.resourceId)}` : "—",
        item.errorMessage || "—",
        formatDateTime(item.createdAt),
      ],
    },
    activity: {
      headers: ["Type", "Summary", "Status", "Details", "Occurred"],
      cells: (item) => [
        <Badge variant="outline">{item.kind}</Badge>,
        activitySummary(item),
        activityStatus(item),
        activityDetail(item),
        formatDateTime(item.occurredAt),
      ],
    },
  };
  const renderer = renderers[resource];
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {renderer.headers.map((header) => (
            <TableHead key={header}>{header}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.items.map((item: any) => (
          <TableRow key={`${item.kind || resource}-${item.id}`}>
            {renderer.cells(item).map((cell, index) => (
              <TableCell key={index}>{cell}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  onPage: (page: number) => void;
}) {
  return (
    <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4 text-sm">
      <span className="text-slate-500">
        {total} records · Page {page} of {totalPages}
      </span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function Detail({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span className={`text-right text-slate-900 ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </span>
    </div>
  );
}
function MiniCount({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-sm bg-slate-50 p-2">
      <p className="text-lg font-semibold text-slate-950">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}
function Empty({ message }: { message: string }) {
  return (
    <div className="rounded-sm border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
      {message}
    </div>
  );
}
function PageMessage({ message, error = false }: { message: string; error?: boolean }) {
  return (
    <div
      className={`rounded-sm border p-6 text-sm ${
        error ? "border-red-200 bg-red-50 text-red-700" : "border-slate-200 text-slate-500"
      }`}
    >
      {message}
    </div>
  );
}
function formatDate(value?: string | Date | null) {
  return value ? new Date(value).toLocaleDateString() : "—";
}
function formatDateTime(value?: string | Date | null) {
  return value ? new Date(value).toLocaleString() : "Never";
}
function shortId(value?: string | null) {
  return value ? (value.length > 16 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value) : "—";
}
function activitySummary(item: any) {
  if (item.kind === "notification") return item.title;
  if (item.kind === "spotlight") return "Spotlight introduction";
  if (item.kind === "skill") return item.skillName;
  return item.title || "Career path";
}
function activityStatus(item: any) {
  if (item.kind === "notification") return item.isRead ? "Read" : "Unread";
  if (item.status) return <Badge variant="outline">{item.status}</Badge>;
  return item.completed ? "Completed" : "In progress";
}
function activityDetail(item: any) {
  if (item.kind === "notification") return item.requiresAction ? "Action required" : item.priority;
  if (item.kind === "skill") return `${Math.round(item.progress)}%`;
  if (item.kind === "career_path") return `${item.completedStages}/${item.totalStages} stages`;
  return item.isActive ? "Active" : "Inactive";
}
